
/**
 * @file ReceiptScanner.tsx
 * @description This component is a stable, production-ready implementation of the receipt scanner.
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 * !! WARNING: This file is considered feature-complete and should not be modified unless absolutely !!
 * !! necessary. Any changes may disrupt the stable functionality of the receipt scanning feature.  !!
 * !! If changes are required, please consult with the team and thoroughly test all functionality.  !!
 * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
 */

import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, RotateCcw, Brain, Sparkles, Receipt, ScanLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";

interface ReceiptScannerProps {
  open: boolean;
  onClose: () => void;
  onCapture: (imageSrc: string) => void;
  isProcessing: boolean;
}

export const ReceiptScanner: React.FC<ReceiptScannerProps> = ({
  open,
  onClose,
  onCapture,
  isProcessing
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [cameraError, setError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
    }
  }, [open]);

  const scannerText = {
    title: i18n.language === 'ja' ? "レシートをスキャン" : "Scan Receipt",
    camera: i18n.language === 'ja' ? "写真を撮る" : "Take Photo",
    upload: i18n.language === 'ja' ? "画像をアップロード" : "Upload Image",
    retake: i18n.language === 'ja' ? "再撮影" : "Retake",
    error: i18n.language === 'ja' ? "エラーが発生しました" : "An error occurred",
    processing: i18n.language === 'ja' ? "処理中..." : "Processing...",
    description: i18n.language === 'ja' ? "レシートの写真を撮るかアップロードしてください" : "Take a photo or upload a receipt image",
    aiPowered: i18n.language === 'ja' ? "AI搭載" : "AI-Powered"
  };

  const convertToDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const triggerCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCameraCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        const dataUrl = await convertToDataURL(file);
        setError(null);
        onCapture(dataUrl);
      }
    } catch (error) {
      console.error("Error capturing from camera:", error);
      setError(i18n.language === 'ja' 
        ? "カメラからの取得中にエラーが発生しました" 
        : "Error capturing from camera");
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        const dataUrl = await convertToDataURL(file);
        setError(null);
        onCapture(dataUrl);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setError(i18n.language === 'ja'
        ? "ファイルのアップロード中にエラーが発生しました"
        : "Error uploading file");
    }
  };

  const retakePhoto = () => {
    setError(null);
  };

  const ScannerContent = () => (
    <div className="flex flex-col">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{scannerText.title}</h2>
          <div className="inline-flex items-center px-2 py-1 rounded-full bg-gradient-to-r from-primary to-green/80 text-white text-xs">
            <Sparkles className="h-3 w-3 mr-1" />
            {scannerText.aiPowered}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="relative flex flex-col bg-gray-100 dark:bg-gray-900 rounded-lg">
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleCameraCapture}
        />
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileUpload}
        />

        {cameraError ? (
          <div className="text-center p-3 text-white bg-red-500/20 rounded-md">
            <p>{scannerText.error}</p>
            <p className="text-sm mt-2">{cameraError}</p>
            <Button 
              variant="outline" 
              className="mt-3" 
              onClick={retakePhoto}
              size="sm"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              {scannerText.retake}
            </Button>
          </div>
        ) : isProcessing ? (
          <div className="p-4 flex flex-col items-center justify-center">
            <div className="relative mb-4 w-24 h-24">
              <div className="absolute inset-0 rounded-xl overflow-hidden backdrop-blur-sm bg-gradient-to-r from-primary/10 to-green-500/10">
                <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-primary/20 to-green-500/20" />
                
                <div className="absolute w-full h-1 bg-gradient-to-r from-primary to-green-500 top-0 animate-[scan_2s_ease-in-out_infinite]" />
                
                <div className="absolute inset-0 flex items-center justify-center">
                  <Receipt className="w-12 h-12 text-primary animate-[pulse_2s_ease-in-out_infinite]" />
                </div>
                
                <div className="absolute inset-0">
                  <ScanLine className="w-full h-full text-primary/30 animate-pulse" />
                </div>
              </div>
            </div>
            <p className="text-base font-medium text-gray-800 dark:text-gray-200 mb-1">
              {t('expenses.processing')}
            </p>
            <p className="text-xs text-muted-foreground text-center max-w-[200px]">
              {t('expenses.scanningDescription')}
            </p>
          </div>
        ) : (
          <div className="p-4 flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 mb-2 flex items-center justify-center rounded-full bg-gradient-to-r from-primary/10 to-green/10 p-3">
              <Brain className="h-10 w-10 text-primary" />
            </div>
            
            <Button 
              size="mobile" 
              onClick={triggerCamera} 
              fullWidth
              rounded="full"
              className="bg-gradient-to-r from-primary to-green hover:from-primary/90 hover:to-green/90 flex justify-center gap-2"
            >
              <Camera className="h-4 w-4" />
              {scannerText.camera}
            </Button>

            <div className="flex items-center w-full">
              <div className="flex-1 h-px bg-gray-300"></div>
              <span className="px-2 text-xs text-gray-500">or</span>
              <div className="flex-1 h-px bg-gray-300"></div>
            </div>

            <Button 
              size="mobile"
              variant="outline" 
              onClick={triggerFileUpload} 
              fullWidth
              rounded="full"
              className="flex justify-center gap-2 text-gray-700 dark:text-gray-300"
            >
              <Upload className="h-4 w-4" />
              {scannerText.upload}
            </Button>
          </div>
        )}
      </div>

      {cameraError && (
        <div className="mt-3 flex justify-center gap-2">
          <Button 
            variant="outline"
            size="mobile" 
            onClick={retakePhoto}
            className="flex-1"
            rounded="full"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            {scannerText.retake}
          </Button>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent 
          side="bottom" 
          className="h-auto p-0 pt-0 rounded-t-xl overflow-hidden" 
          showCloseButton={false}
        >
          <div className="p-3 flex flex-col">
            <ScannerContent />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-sm p-0 rounded-lg overflow-hidden">
        <DialogTitle className="sr-only">{scannerText.title}</DialogTitle>
        <div className="p-3">
          <ScannerContent />
        </div>
      </DialogContent>
    </Dialog>
  );
};
